import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

export function CreateServiceDialog() {
  const [open, setOpen] = useState(false);
  const { formError, showFormError, clearFormError } = useFormError(open);
  const [name, setName] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (serviceName: string) => {
      const { data } = await api.post('/commercial/services', { name: serviceName });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast({ variant: 'success', title: 'Sucesso', description: 'Serviço criado com sucesso' });
      setName('');
      setOpen(false);
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      if (status === 409) {
        showFormError('Serviço com este nome já existe');
      } else {
        showFormError('Falha ao criar serviço');
      }
    },
  });

  const handleSubmit = () => {
    clearFormError();
    const trimmed = name.trim();
    if (!trimmed) return;
    mutation.mutate(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          Novo serviço
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo serviço</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="service-name">Nome do serviço</Label>
          <Input
            id="service-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do serviço"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        <FormError error={formError} onDismiss={clearFormError} />
        <DialogFooter>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isPending || !name.trim()}
          >
            {mutation.isPending ? 'Criando...' : 'Criar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
